import React from 'react'
import type { ReconciliationFieldVO } from '../../../apis/types'
import { statusStyles, sourceDisplayNames } from './reconciliation.types'

interface ReconciliationCellProps {
    field: ReconciliationFieldVO
    fieldKey: string
    lastPaymentAmount?: ReconciliationFieldVO
    isInactive?: boolean
    // For fieldKey === 'scraperLinked' only. When false (no scraper_account
    // row), the cell renders a 'never seen by scraper' subtitle below the
    // 'Not linked' badge so ops can distinguish a never-seen account from
    // one that was dropped by a recent scrape. WEB-6098.
    scraperAccountExists?: boolean
    // For fieldKey === 'scraperLinked' only. True when row.issues contains
    // 'scraperNotLinked' (i.e. billing mode requires the link). Drives both
    // the badge color (red vs gray) and the cell background (red-tinted vs
    // neutral) so the cell-level severity matches the badge-level severity
    // instead of inheriting the green 'exact' background. WEB-6098.
    scraperLinkedIsIssue?: boolean
}

const formatValue = (value: string | number | boolean | null | undefined): string => {
    if (value == null) return ''
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') return String(value)
    return value
}

const formatDate = (value: string | number | boolean | null | undefined): string => {
    if (value == null || typeof value !== 'string') return ''
    const d = new Date(value)
    if (isNaN(d.getTime())) return String(value)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatMoney = (value: string | number | boolean | null | undefined): string => {
    if (value == null) return ''
    const n = typeof value === 'number' ? value : parseFloat(String(value))
    if (isNaN(n)) return String(value)
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const Badge: React.FC<{ text: string; color: 'green' | 'yellow' | 'red' | 'gray' }> = ({ text, color }) => {
    const styles = {
        green: 'bg-green-100 dark:bg-green-400/10 text-green-700 dark:text-green-400 ring-green-600/10 dark:ring-green-400/20',
        yellow: 'bg-yellow-100 dark:bg-yellow-400/10 text-yellow-700 dark:text-yellow-400 ring-yellow-600/10 dark:ring-yellow-400/20',
        red: 'bg-red-100 dark:bg-red-400/10 text-red-700 dark:text-red-400 ring-red-600/10 dark:ring-red-400/20',
        gray: 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 ring-slate-300 dark:ring-slate-600',
    }
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${styles[color]}`}>
            {text}
        </span>
    )
}

const vaTableStatusColor = (status: string): 'green' | 'yellow' | 'red' | 'gray' => {
    const lower = status.toLowerCase()
    if (lower === 'active' || lower === 'billing') return 'green'
    if (lower === 'submitted') return 'yellow'
    if (lower === 'ended') return 'gray'
    return 'gray'
}

const paymentStatusColor = (status: string): 'green' | 'yellow' | 'red' | 'gray' => {
    const lower = status.toLowerCase()
    if (lower === 'processed') return 'green'
    if (lower === 'returned') return 'red'
    if (lower === 'pending') return 'yellow'
    return 'gray'
}

const ReconciliationCell: React.FC<ReconciliationCellProps> = ({ field, fieldKey, lastPaymentAmount, isInactive, scraperAccountExists, scraperLinkedIsIssue }) => {
    const style = statusStyles[field.status]
    const source = sourceDisplayNames[field.authoritativeSource]

    if (field.status === 'sourceUnavailable') {
        return (
            <td className={`px-4 py-3 text-center ${style.bg}`} title={`${source} data unavailable — scraper not linked for this account.`}>
                <Badge text="N/A" color="gray" />
            </td>
        )
    }

    if (field.status === 'missing') {
        if (isInactive) {
            return (
                <td className="px-4 py-3 text-center" title="Inactive meter — data not expected.">
                    <span className="text-slate-300 dark:text-slate-600">&mdash;</span>
                </td>
            )
        }
        return (
            <td className={`px-4 py-3 ${style.bg}`} title={`Missing in ${source}. Check the source system and update.`}>
                <Badge text="MISSING" color="red" />
                <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">in {source}</div>
            </td>
        )
    }

    if (field.status === 'mismatch' && field.mismatch) {
        const authSource = source
        const comparisonSourceKey = Object.entries(field.values)
            .filter(([key]) => key !== field.authoritativeSource)
            .find(([, val]) => val != null)?.[0]
        const compSourceLabel = comparisonSourceKey ? sourceDisplayNames[comparisonSourceKey as keyof typeof sourceDisplayNames] ?? comparisonSourceKey : ''

        const tooltip = field.mismatch.remediationHintKey
            ? `How to fix: Update the value in the non-authoritative source to match ${authSource}.`
            : undefined

        return (
            <td className={`px-4 py-3 text-xs ${style.bg} ${tooltip ? 'cursor-help' : ''}`} title={tooltip}>
                <div>
                    <div className="text-slate-700 dark:text-slate-300">
                        {formatValue(field.mismatch.authoritativeValue)}{' '}
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">{authSource}</span>
                    </div>
                    <div className="text-orange-500 text-[10px] font-bold leading-tight my-0.5">&ne;</div>
                    <div className="text-slate-700 dark:text-slate-300">
                        {formatValue(field.mismatch.comparisonValue)}{' '}
                        <span className="text-[10px] text-slate-400 dark:text-slate-500">{compSourceLabel}</span>
                    </div>
                </div>
            </td>
        )
    }

    // VA Table Status: color-coded badge
    if (fieldKey === 'vaTableStatus' && typeof field.display === 'string') {
        const vaColor = vaTableStatusColor(field.display)
        return (
            <td className={`px-4 py-3 text-center ${style.bg}`} title={`From ${source}`}>
                <Badge text={field.display} color={vaColor} />
            </td>
        )
    }

    // Payment status: badge rendering
    if (fieldKey === 'paymentStatus' && typeof field.display === 'string') {
        const color = paymentStatusColor(field.display)
        const tooltip = field.display === 'Returned'
            ? 'Payment was returned by the utility. May indicate insufficient funds — follow up with tenant.'
            : field.display === 'Pending'
                ? 'Payment is pending — may still clear or be returned.'
                : `From ${source}`
        return (
            <td className={`px-4 py-3 text-center ${field.display === 'Returned' ? 'bg-red-50 dark:bg-red-900/20' : field.display === 'Pending' ? 'bg-yellow-50 dark:bg-yellow-900/20' : style.bg}`} title={tooltip}>
                <Badge text={field.display} color={color} />
            </td>
        )
    }

    // Last Payment Date: composite cell with amount underneath
    if (fieldKey === 'lastPaymentDate') {
        const dateStr = formatDate(field.display)
        const amountStr = lastPaymentAmount ? formatMoney(lastPaymentAmount.display) : ''
        if (!dateStr) {
            return (
                <td className={`px-4 py-3 text-xs ${style.bg}`} title={`From ${source}`}>
                    <span className="text-slate-300 dark:text-slate-600">&mdash;</span>
                </td>
            )
        }
        return (
            <td className={`px-4 py-3 text-xs ${style.bg}`} title={`From ${source}`}>
                <div className="text-slate-700 dark:text-slate-300">{dateStr}</div>
                {amountStr && <div className="text-slate-500 dark:text-slate-400">{amountStr}</div>}
            </td>
        )
    }

    // Statement Due Date: date formatting
    if (fieldKey === 'lastStmtDueDate') {
        const dateStr = formatDate(field.display)
        return (
            <td className={`px-4 py-3 text-xs text-slate-600 dark:text-slate-400 ${style.bg}`} title={`From ${source}`}>
                {dateStr || <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
            </td>
        )
    }

    // Last Statement Date: date formatting
    if (fieldKey === 'lastStmtDate') {
        const dateStr = formatDate(field.display)
        return (
            <td className={`px-4 py-3 text-xs text-slate-600 dark:text-slate-400 ${style.bg}`} title={`From ${source}`}>
                {dateStr || <span className="text-slate-300 dark:text-slate-600">&mdash;</span>}
            </td>
        )
    }

    if (field.status === 'fuzzy') {
        return (
            <td className={`px-4 py-3 text-sm ${style.bg} ${style.text}`} title={`Value from ${source}. Fuzzy match (minor formatting difference).`}>
                {formatValue(field.display)}
            </td>
        )
    }

    const displayVal = formatValue(field.display)

    // scraperLinked: cell-level severity matches the badge. WEB-6098 keeps
    // field.status='exact' for both true and false, but the cell visuals need
    // to reflect whether 'Not linked' is a real issue (billing mode requires
    // the link → red bg + red badge) vs a silenced one (estimated/balance-only
    // billing → neutral bg + gray badge). Subtitle 'never seen by scraper'
    // appears only when no scraper_account row exists, distinguishing it from
    // the 'no longer linked' (is_linked=false) case where the binary speaks
    // for itself.
    if (fieldKey === 'scraperLinked' && typeof field.display === 'boolean') {
        const isLinked = field.display === true
        const cellBg = isLinked
            ? 'bg-green-50 dark:bg-green-900/20'
            : scraperLinkedIsIssue
                ? 'bg-red-50 dark:bg-red-900/20'
                : 'bg-slate-50 dark:bg-slate-800/50'
        const badgeText = isLinked ? 'Linked' : 'Not linked'
        const badgeColor: 'green' | 'red' | 'gray' = isLinked
            ? 'green'
            : scraperLinkedIsIssue ? 'red' : 'gray'
        const showNeverSeenSubtitle = !isLinked && scraperAccountExists === false
        return (
            <td className={`px-4 py-3 text-center ${cellBg}`} title={`From ${source}`}>
                <Badge text={badgeText} color={badgeColor} />
                {showNeverSeenSubtitle && (
                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">never seen by scraper</div>
                )}
            </td>
        )
    }

    // Boolean fields: badge rendering
    if (typeof field.display === 'boolean') {
        const badgeText = fieldKey === 'autopay'
            ? (field.display ? 'Yes' : 'No')
            : (field.display ? 'Yes' : 'No')
        const badgeColor = fieldKey === 'autopay'
            ? (field.display ? 'green' : 'yellow')
            : (field.display ? 'green' : 'gray')
        return (
            <td className={`px-4 py-3 text-center ${style.bg}`} title={`From ${source}`}>
                <Badge text={badgeText} color={badgeColor} />
            </td>
        )
    }

    // Numeric fields (vnemPct)
    if (typeof field.display === 'number') {
        return (
            <td className={`px-4 py-3 text-center ${style.bg}`} title={`From ${source}. ${displayVal}% of property generation.`}>
                <span className="text-sm font-medium dark:text-slate-300">{displayVal}%</span>
            </td>
        )
    }

    return (
        <td className={`px-4 py-3 text-xs ${style.bg} ${style.text}`} title={`From ${source}`}>
            <span className="line-clamp-2">{displayVal}</span>
        </td>
    )
}

export default ReconciliationCell
